import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, CircleNotch, Play, CheckCircle, XCircle, WarningCircle, Lightbulb, CaretDown,
} from "@phosphor-icons/react";

function ScoreRing({ score }) {
  const color = score >= 80 ? "#16A34A" : score >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${score} 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display font-extrabold text-xl" style={{ color }}>{score}</div>
    </div>
  );
}

export default function QASimulator({ agentId }) {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [n, setN] = useState(5);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get(`/agents/${agentId}/simulation`).then((r) => setResult(r.data)).catch(() => {});
  }, [agentId]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/simulate?n=${n}`);
      setResult(data);
      toast.success(`Simulering klar — ${data.total} samtal testade`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte köra simuleringen");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-5" data-testid="qa-simulator">
      <div className="rounded-xl border border-[#0033FF]/20 bg-gradient-to-br from-[#0033FF]/[0.04] to-transparent p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><ShieldCheck size={20} weight="bold" className="text-[#0033FF]" /> QA-arena</h3>
            <p className="text-sm text-zinc-500 mt-0.5 max-w-xl">Kör simulerade kundsamtal mot agenten och få en kvalitetsrapport med svagheter och kunskapsluckor — innan du går live.</p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="qa-count" value={n} onChange={(e) => setN(Number(e.target.value))}
              className="bg-white border border-zinc-200 rounded-md px-2.5 py-2 text-sm font-semibold">
              {[3, 5, 8].map((v) => <option key={v} value={v}>{v} samtal</option>)}
            </select>
            <button data-testid="qa-run-button" onClick={run} disabled={running}
              className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-60">
              {running ? <CircleNotch size={15} className="animate-spin" /> : <Play size={15} weight="fill" />}
              {running ? "Kör simulering…" : "Kör simulering"}
            </button>
          </div>
        </div>
        {running && <p className="text-xs text-zinc-400 mt-3">AI:n spelar olika kunder och betygsätter agentens svar — detta tar ~20–40 sekunder.</p>}
      </div>

      {result && result.total > 0 && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-5 flex items-center gap-4" data-testid="qa-overall">
              <ScoreRing score={result.overall_score} />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Totalbetyg</div>
                <div className="text-sm text-zinc-500 mt-1">{result.overall_score >= 80 ? "Redo att lansera 🎉" : result.overall_score >= 60 ? "Behöver finjustering" : "Behöver arbete"}</div>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Godkända</div>
              <div className="font-display font-extrabold text-3xl mt-1">{result.pass_rate}%</div>
              <div className="text-xs text-zinc-400 mt-1">{result.passed}/{result.total} samtal</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Kunskapsluckor</div>
              <div className="font-display font-extrabold text-3xl mt-1">{result.kb_gaps?.length || 0}</div>
              <div className="text-xs text-zinc-400 mt-1">upptäckta brister</div>
            </div>
          </div>

          {result.kb_gaps?.length > 0 && (
            <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4" data-testid="qa-kb-gaps">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-600 flex items-center gap-1.5 mb-2"><Lightbulb size={14} weight="fill" /> Föreslagna kunskapsluckor att fylla</div>
              <ul className="space-y-1.5">
                {result.kb_gaps.map((g, i) => (
                  <li key={i} className="text-sm text-zinc-700 flex items-start gap-2"><WarningCircle size={15} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />{g}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Testresultat</div>
            <div className="divide-y divide-zinc-50">
              {result.results.map((r, i) => (
                <div key={i} data-testid="qa-result-row">
                  <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50">
                    {r.passed ? <CheckCircle size={18} weight="fill" className="text-green-500 shrink-0" /> : <XCircle size={18} weight="fill" className="text-red-500 shrink-0" />}
                    <span className="flex-1 text-sm text-zinc-700 truncate">{r.question}</span>
                    <span className="text-sm font-bold shrink-0" style={{ color: r.score >= 80 ? "#16A34A" : r.score >= 60 ? "#F59E0B" : "#EF4444" }}>{r.score}</span>
                    <CaretDown size={14} className={`text-zinc-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
                  </button>
                  {open === i && (
                    <div className="px-4 pb-4 pl-12 space-y-2 text-sm">
                      <div><span className="text-xs font-bold uppercase text-zinc-400">Agentens svar:</span><p className="text-zinc-600 mt-0.5">{r.answer}</p></div>
                      {r.issue && <div className="text-amber-700 bg-amber-50 rounded px-2 py-1.5"><b>Problem:</b> {r.issue}</div>}
                      <span className="inline-block text-[11px] font-semibold bg-zinc-100 text-zinc-500 rounded-full px-2 py-0.5">{r.category}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {result && result.total === 0 && (
        <p className="text-sm text-zinc-400" data-testid="qa-empty">Lägg till kunskap och kör en simulering för att se hur agenten klarar sig.</p>
      )}
    </div>
  );
}
